import { firestore } from '@src/api/Firebase/Firebase';
import { collection } from 'firebase/firestore';
import { normalizePhone } from '@api/Customers/Customer';

export interface BranchOffice {
    id?: string;
    number: string;
    status: boolean;
    name: string;
    address: string;
    googlePlaceId: string;
    addressDetails: {
        street: string;
        city: string;
        state: string;
        country: string;
        zipCode: string;
    };
    location: {
        lat: number;
        lng: number;
    };
    phone: string;
    distributionCenterId: string;
    hasPickup: boolean;
    hasDelivery: boolean;
    deactivationReason?: string;
    delivery?: {
        initialFee: number;
        costPerKm: number;
        maxDistance: number;
        freeDeliveryEnabled?: boolean;
        minAmountForFreeDelivery?: number;
    };
    schedule: {
        [key: string]: {
            enabled: boolean;
            openingTime: string;
            closingTime: string;
        };
    };
    manager: {
        name: string;
        phone: string;
        email: string;
    };
    // Notificar al responsable (manager) de nuevos pedidos por WhatsApp.
    // Ausente/undefined = true (habilitado por default); solo false lo apaga.
    notifyManagerOnNewOrder?: boolean;
    /**
     * Descuento de inventario ENCENDIDO para esta sucursal (F5, §4.2). Ausente o
     * false = las ventas se explotan y se avisan pero NO tocan el stock. Se
     * enciende sucursal por sucursal DESPUÉS de su conteo físico inicial: el
     * ledger arranca de un saldo real, no de un número heredado sin auditar.
     */
    inventoryDiscountEnabled?: boolean;
    /**
     * Umbrales en DÍAS de inventario para Crítico y Bajo (plan de inventario F4). Los
     * ajusta el admin de sucursal (NO es campo protegido: `BranchEdit`); ausente = el
     * default de `@api/Inputs/StockStatus` (2 y 5). Decisión del negocio, 13-oct-2026.
     */
    stockDays?: { critical: number; low: number };
    // Insumos marcados como AGOTADOS en esta sucursal ("86"): ids de `inputs`.
    // Ausente = ninguno. La disponibilidad del menú 2.0 se deriva de aquí
    // (ver @api/Catalog/Availability — web 2.0 replica el módulo en F3).
    unavailableInputs?: string[];
}

export const BRANCH_OFFICES_COLLECTION = 'branch_offices';

export const branchOfficesRef = collection(firestore, BRANCH_OFFICES_COLLECTION);

/**
 * Deja el teléfono del responsable en la IDENTIDAD de 10 dígitos antes de
 * guardarlo.
 *
 * ⚠️ Nace de un fallo real (18-sep): "Centro Histórico (Desayunos)" tenía
 * `+52 1 221 106 2577` capturado a mano, el server le concatenaba `+52` y salía
 * `whatsapp:+52+52 1 221 10…`. Twilio lo rechazaba y **el responsable llevaba
 * sin recibir un solo aviso de pedido nuevo, sin que nadie lo notara.**
 *
 * Va en el SERVICIO y no en el formulario porque la sucursal se guarda desde dos
 * pantallas (el atajo del sidebar y Sucursales): un candado sobre el botón deja
 * el otro camino abierto — la lección del gate del cobro.
 *
 * Devuelve una COPIA: el objeto puede venir del store, que Redux congela.
 */
export const withNormalizedManagerPhone = (branchOffice: BranchOffice): BranchOffice => {
    const phone = branchOffice.manager?.phone;
    if (!phone) return branchOffice;
    const normalizado = normalizePhone(phone);
    if (normalizado === phone) return branchOffice;
    return { ...branchOffice, manager: { ...branchOffice.manager, phone: normalizado } };
};

/**
 * Lo de la sucursal que NO es de la sucursal (plan de Gastos §8.7): el número (prefijo
 * del folio), el CEDIS que la surte y el descuento de inventario (va tras el conteo
 * físico). Solo el admin general los cambia (`Capability.BranchEditProtected`).
 */
export const BRANCH_PROTECTED_FIELDS = ['number', 'distributionCenterId', 'inventoryDiscountEnabled'] as const;

/**
 * La sucursal a guardar con los campos protegidos COMO ESTABAN. Es el gate de
 * `saveBranchOffice` para quien no los puede cambiar: aunque la pantalla los mande
 * distintos, no pasan. Devuelve una copia (el objeto puede venir del store).
 */
export const keepProtectedBranchFields = (next: BranchOffice, previous: BranchOffice): BranchOffice => {
    const copy: BranchOffice = { ...next };
    BRANCH_PROTECTED_FIELDS.forEach((field) => {
        if (previous[field] === undefined) delete copy[field];
        else (copy as unknown as Record<string, unknown>)[field] = previous[field];
    });
    return copy;
};
